/**
 * Il banco di misura della fase 1.
 *
 * «Non a occhio, e non sul computer di chi scrive» (07-PRESTAZIONI §8). Questa schermata
 * **stampa i numeri**, e gira sul dispositivo perché i numeri presi altrove non dicono
 * niente.
 *
 * Cosa misura, e come:
 *
 *  - **Ritardo del fuoco.** `t0` è l'istante in cui `FocusPort` consegna la freccia; `t1`
 *    è l'inizio della cornice successiva alla scrittura del `transform`, cioè il momento
 *    in cui il browser sta per comporre il riquadro nella posizione nuova. Fra i due c'è
 *    tutto il lavoro *e* l'attesa del sincronismo verticale, che è quello che la persona
 *    sente. **Mediana e 95° percentile, non la media**: la media nasconde proprio i
 *    cinque spostamenti lenti su cento che fanno premere due volte.
 *  - **Cornici perse.** Un giro continuo su `requestAnimationFrame` conta gli intervalli
 *    che superano il budget di 16,7 ms.
 *  - **Tempo al primo fotogramma**, e **se il canale era acceso o spento**: senza questa
 *    distinzione la media non significa niente (06-RIPRODUZIONE §10).
 *  - **Notifiche di cursore contro notifiche di finestra.** È la dimostrazione che il
 *    fuoco non dipende da un ri-rendering della griglia: se scorrendo a lungo i due
 *    numeri si somigliano, la virtualizzazione non sta facendo il suo mestiere.
 *
 * «Scorri a lungo» inietta le frecce attraverso `FocusPort` e non con un evento di
 * tastiera sintetico: la misura deve passare dalla stessa porta che usa il telecomando,
 * altrimenti misura un'altra cosa.
 */

import { useCallback, useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import {
  windowWidthPx,
  FrameRecorder,
  LatencyRecorder,
  PHASE1_TARGETS,
  meetsFocusTarget,
  summarize,
  type Channel,
  type FrameSummary,
  type GuideWindow,
  type LatencySummary,
  type ProgramStore,
} from '@player/domain';
import type { FocusPort, PlayerPort, RemoteKey } from '@player/ports';
import { FONT_STACK, colors } from '../theme.js';
import { useTranslator } from '../i18n/I18nProvider.js';
import { GuideController } from '../guide/GuideController.js';
import { GuideGrid } from '../guide/GuideGrid.js';
import { GuidePreview } from '../guide/GuidePreview.js';

/** Intervallo fra due frecce iniettate: più stretto della ripetizione di un telecomando,
 *  che sta intorno ai 100 ms. Se regge qui, regge sul divano. */
const AUTO_SCROLL_INTERVAL_MS = 60;
const AUTO_SCROLL_PRESSES = 600;

export interface BenchScreenProps {
  readonly focusPort: FocusPort;
  readonly channels: ReadonlyArray<Channel>;
  readonly programs: ProgramStore;
  readonly window: GuideWindow;
  readonly serverNowMs: number;
  readonly createPlayer: (video: HTMLVideoElement) => PlayerPort;
  readonly platformLabel: string;
  readonly missingKeys: ReadonlySet<string>;
}

export function BenchScreen({
  focusPort,
  channels,
  programs,
  window: guideWindow,
  serverNowMs,
  createPlayer,
  platformLabel,
  missingKeys,
}: BenchScreenProps): ReactNode {
  const translator = useTranslator();
  const containerRef = useRef<HTMLDivElement | null>(null);
  const videoRef = useRef<HTMLVideoElement | null>(null);

  const controller = useMemo(
    () =>
      new GuideController({
        channels,
        programs,
        window: guideWindow,
        viewport: { heightPx: 700, timelineWidthPx: windowWidthPx(guideWindow) },
      }),
    [channels, programs, guideWindow],
  );
  useEffect(() => () => controller.dispose(), [controller]);

  const focusLatency = useRef(new LatencyRecorder());
  const frames = useRef(new FrameRecorder());
  const firstFrameWarm = useRef<number[]>([]);
  const firstFrameCold = useRef<number[]>([]);

  const [focusSummary, setFocusSummary] = useState<LatencySummary>(() => summarize([]));
  const [frameSummary, setFrameSummary] = useState<FrameSummary>(() => frames.current.summary());
  const [counters, setCounters] = useState(() => controller.counters);
  const [autoScrolling, setAutoScrolling] = useState(false);
  const [streamUrl, setStreamUrl] = useState('');
  const [streamNote, setStreamNote] = useState('');
  const [channelWasWarm, setChannelWasWarm] = useState(true);

  // ── Ritardo del fuoco ──────────────────────────────────────────────────────
  useEffect(() => {
    const handle = (key: RemoteKey): boolean => {
      if (key.kind !== 'direction') return false;
      const pressedAt = performance.now();
      const direction = key.direction;
      if (direction === 'up' || direction === 'down' || direction === 'left' || direction === 'right') {
        const moved = controller.move(direction);
        if (!moved) return true;
      }
      requestAnimationFrame(() => {
        focusLatency.current.add(performance.now() - pressedAt);
      });
      return true;
    };
    return focusPort.subscribe(handle);
  }, [focusPort, controller]);

  // ── Cornici ────────────────────────────────────────────────────────────────
  useEffect(() => {
    let running = true;
    const tick = (timestamp: number): void => {
      if (!running) return;
      frames.current.tick(timestamp);
      requestAnimationFrame(tick);
    };
    requestAnimationFrame(tick);
    return () => {
      running = false;
    };
  }, []);

  // I numeri si rinfrescano due volte al secondo: rinfrescarli a ogni campione
  // ri-renderebbe la pagina dentro la misura, e la misura misurerebbe sé stessa.
  useEffect(() => {
    const timer = setInterval(() => {
      setFocusSummary(focusLatency.current.summary());
      setFrameSummary(frames.current.summary());
      setCounters(controller.counters);
    }, 500);
    return () => clearInterval(timer);
  }, [controller]);

  // ── Scorri a lungo ─────────────────────────────────────────────────────────
  useEffect(() => {
    if (!autoScrolling) return undefined;
    let pressesLeft = AUTO_SCROLL_PRESSES;
    let downwards = true;
    const timer = setInterval(() => {
      if (pressesLeft <= 0) {
        setAutoScrolling(false);
        return;
      }
      pressesLeft -= 1;
      const cursor = controller.currentCursor;
      if (downwards && cursor.channelIndex >= controller.channelCount - 1) downwards = false;
      if (!downwards && cursor.channelIndex <= 0) downwards = true;
      focusPort.injectForMeasurement({ kind: 'direction', direction: downwards ? 'down' : 'up' });
    }, AUTO_SCROLL_INTERVAL_MS);
    return () => clearInterval(timer);
  }, [autoScrolling, focusPort, controller]);

  const reset = useCallback(() => {
    focusLatency.current.reset();
    frames.current.reset();
    firstFrameWarm.current = [];
    firstFrameCold.current = [];
    controller.resetCounters();
    setFocusSummary(summarize([]));
    setFrameSummary(frames.current.summary());
    setCounters(controller.counters);
    setStreamNote('');
  }, [controller]);

  // ── Riproduzione ───────────────────────────────────────────────────────────
  const playerRef = useRef<PlayerPort | null>(null);
  const play = useCallback(() => {
    const video = videoRef.current;
    if (!video || streamUrl.trim().length === 0) return;
    playerRef.current?.destroy();
    const player = createPlayer(video);
    playerRef.current = player;
    player.subscribe((event) => {
      if (event.type === 'firstFrame') {
        const bucket = channelWasWarm ? firstFrameWarm : firstFrameCold;
        bucket.current.push(event.atMs);
        setStreamNote('');
      }
      if (event.type === 'error') {
        setStreamNote(translator.t(`errors.${event.error.code}`, event.error.params));
      }
    });
    player.play(streamUrl.trim());
  }, [createPlayer, streamUrl, channelWasWarm, translator]);

  const stop = useCallback(() => {
    playerRef.current?.stop();
  }, []);

  useEffect(() => () => playerRef.current?.destroy(), []);

  const focusWithinTarget = meetsFocusTarget(focusSummary);

  return (
    <div
      ref={containerRef}
      style={{
        display: 'flex',
        flexDirection: 'column',
        height: '100%',
        background: colors.background,
        color: colors.text,
        padding: '32px 40px',
        gap: 20,
        boxSizing: 'border-box',
      }}
    >
      <header style={{ display: 'flex', alignItems: 'baseline', gap: 20, flexWrap: 'wrap' }}>
        <h1 style={{ font: `800 40px ${FONT_STACK}`, margin: 0, letterSpacing: '-.02em' }}>
          {translator.t('bench.title')}
        </h1>
        <span style={{ font: `500 20px ${FONT_STACK}`, color: colors.textDim }}>
          {translator.t('bench.subtitle')}
        </span>
      </header>

      <section style={{ display: 'flex', gap: 16, flexWrap: 'wrap' }}>
        <Tile label={translator.t('bench.device')} value={platformLabel} />
        <Tile label={translator.t('bench.channels')} value={translator.number(channels.length)} />
        <Tile
          label={translator.t('bench.focusLatency')}
          value={`${format(focusSummary.p95Ms)} ms`}
          hint={
            `${translator.t('bench.median')} ${format(focusSummary.medianMs)} · ` +
            `${translator.t('bench.max')} ${format(focusSummary.maxMs)} · ` +
            `${translator.number(focusSummary.count)} ${translator.t('bench.samples')}`
          }
          verdict={
            focusSummary.count === 0
              ? null
              : {
                  ok: focusWithinTarget,
                  text: `${translator.t('bench.target')} ${PHASE1_TARGETS.focusLatencyP95Ms} ms · ${
                    focusWithinTarget ? translator.t('bench.pass') : translator.t('bench.fail')
                  }`,
                }
          }
        />
        <Tile
          label={translator.t('bench.frames')}
          value={`${translator.number(frameSummary.droppedFrames)} ${translator.t('bench.dropped')}`}
          hint={
            `${translator.t('bench.longestGap')} ${format(frameSummary.longestGapMs)} ms · ` +
            `${format(frameSummary.effectiveFps)} ${translator.t('bench.fps')}`
          }
          verdict={
            frameSummary.frames === 0
              ? null
              : {
                  ok: frameSummary.droppedFrames === 0,
                  text:
                    frameSummary.droppedFrames === 0
                      ? translator.t('bench.pass')
                      : translator.t('bench.fail'),
                }
          }
        />
        <Tile
          label={translator.t('bench.firstFrameWarm')}
          value={summaryText(firstFrameWarm.current, translator.t('bench.notMeasured'))}
          hint={`${translator.t('bench.target')} ${PHASE1_TARGETS.firstFrameWarmMs} ms`}
        />
        <Tile
          label={translator.t('bench.firstFrameCold')}
          value={summaryText(firstFrameCold.current, translator.t('bench.notMeasured'))}
          hint={`${translator.t('bench.target')} ${PHASE1_TARGETS.firstFrameColdMs} ms`}
        />
        <Tile
          label="cursor / window"
          value={`${translator.number(counters.cursorNotifications)} / ${translator.number(counters.windowNotifications)}`}
          hint="frecce contro ri-rendering della griglia"
        />
        <Tile
          label={translator.t('bench.missingKeys')}
          value={translator.number(missingKeys.size)}
          hint={[...missingKeys].slice(0, 3).join(' · ')}
        />
      </section>

      <section style={{ display: 'flex', gap: 12, alignItems: 'center', flexWrap: 'wrap' }}>
        <button type="button" data-f="1" onClick={() => setAutoScrolling((value) => !value)} style={actionButton}>
          {autoScrolling ? translator.t('bench.autoScrollStop') : translator.t('bench.autoScroll')}
        </button>
        <button type="button" data-f="1" onClick={reset} style={actionButton}>
          {translator.t('bench.reset')}
        </button>
        <input
          value={streamUrl}
          onChange={(event) => setStreamUrl(event.target.value)}
          placeholder={translator.t('bench.streamUrl')}
          style={{
            flex: 1,
            minWidth: 280,
            height: 48,
            padding: '0 16px',
            borderRadius: 12,
            border: `2px solid ${colors.border}`,
            background: colors.surface,
            color: colors.text,
            font: '500 18px ui-monospace, Menlo, monospace',
          }}
        />
        <label style={{ display: 'flex', alignItems: 'center', gap: 8, font: `500 18px ${FONT_STACK}`, color: colors.textDim }}>
          <input
            type="checkbox"
            checked={channelWasWarm}
            onChange={(event) => setChannelWasWarm(event.target.checked)}
          />
          {translator.t('bench.firstFrameWarm')}
        </label>
        <button type="button" data-f="1" onClick={play} style={actionButton}>
          {translator.t('bench.playStream')}
        </button>
        <button type="button" data-f="1" onClick={stop} style={actionButton}>
          {translator.t('bench.stopStream')}
        </button>
      </section>

      {streamNote ? (
        <div style={{ font: `500 20px ${FONT_STACK}`, color: colors.errorText }}>{streamNote}</div>
      ) : null}

      <div style={{ display: 'flex', gap: 20, minHeight: 0, flex: 1 }}>
        <div style={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column', gap: 16 }}>
          <GuidePreview
            controller={controller}
            programs={programs}
            serverNowMs={serverNowMs}
            reminderIds={EMPTY_REMINDERS}
          />
          <GuideGrid
            controller={controller}
            programs={programs}
            window={guideWindow}
            serverNowMs={serverNowMs}
          />
        </div>
        <video
          ref={videoRef}
          playsInline
          muted
          style={{ flex: 'none', width: 480, height: 270, background: '#000', borderRadius: 12 }}
        />
      </div>
    </div>
  );
}

const EMPTY_REMINDERS: ReadonlySet<string> = new Set<string>();

function summaryText(samples: ReadonlyArray<number>, emptyText: string): string {
  if (samples.length === 0) return emptyText;
  const summary = summarize(samples);
  return `${format(summary.medianMs)} ms`;
}

function format(value: number): string {
  return value >= 100 ? value.toFixed(0) : value.toFixed(1);
}

function Tile({
  label,
  value,
  hint,
  verdict,
}: {
  label: string;
  value: string;
  hint?: string;
  verdict?: { ok: boolean; text: string } | null;
}): ReactNode {
  return (
    <div
      style={{
        flex: '1 1 220px',
        minWidth: 220,
        background: colors.surface,
        borderRadius: 16,
        padding: '16px 20px',
        display: 'flex',
        flexDirection: 'column',
        gap: 6,
      }}
    >
      <span style={{ font: `600 15px ${FONT_STACK}`, color: colors.textDim, letterSpacing: '.06em', textTransform: 'uppercase' }}>
        {label}
      </span>
      <span style={{ font: `800 32px ${FONT_STACK}`, letterSpacing: '-.02em' }}>{value}</span>
      {hint ? <span style={{ font: `500 15px ${FONT_STACK}`, color: colors.textFaint }}>{hint}</span> : null}
      {verdict ? (
        <span style={{ font: `600 15px ${FONT_STACK}`, color: verdict.ok ? colors.success : colors.errorText }}>
          {verdict.text}
        </span>
      ) : null}
    </div>
  );
}

const actionButton = {
  height: 48,
  padding: '0 24px',
  borderRadius: 12,
  border: 0,
  background: colors.surfaceRaised,
  color: colors.text,
  font: `600 18px ${FONT_STACK}`,
  cursor: 'pointer',
} as const;
